import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, Users, Ticket, ArrowLeft, Minus, Plus,
  ShoppingCart, Star, Share2, CheckCircle, AlertCircle, Loader2, Info
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import { api } from '../../lib/api';
import { useCartStore } from '../../store/cart';
import { useUIStore } from '../../store/ui';

interface TicketType {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface Event {
  id: string;
  name: string;
  slug: string;
  category: string;
  description?: string;
  event_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location: string;
  capacity: number;
  spots_left: number;
  image_id?: string;
  featured: number;
  status: string;
  ticketTypes: TicketType[];
}

interface TicketSelection {
  ticketTypeId: string;
  ticketTypeName: string;
  price: number;
  quantity: number;
}

// Default images by category
const DEFAULT_IMAGES: Record<string, string> = {
  'V8 Supercars': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  'Drag Racing': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1200&q=80',
  'Motorcycle': 'https://images.unsplash.com/photo-1558980664-769d59546b3d?w=1200&q=80',
  'Drifting': 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1200&q=80',
  'Track Day': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80',
  'Driver Training': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80',
  'Corporate': 'https://images.unsplash.com/photo-1493238792000-8113da705763?w=1200&q=80',
  'Other': 'https://images.unsplash.com/photo-1518830246650-b54e3b0de5e0?w=1200&q=80',
};

export function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);
  const showToast = useUIStore((s) => s.showToast);

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selections, setSelections] = useState<TicketSelection[]>([]);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    if (slug) {
      loadEvent();
    }
  }, [slug]);

  async function loadEvent() {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/public/events/${slug}`);
      if (res.success) {
        setEvent(res.data);
        // Initialize selections with 0 quantity for each ticket type
        setSelections(
          res.data.ticketTypes.map((tt: TicketType) => ({
            ticketTypeId: tt.id,
            ticketTypeName: tt.name,
            price: tt.price,
            quantity: 0,
          }))
        );
      } else {
        setError(res.error || 'Event not found');
      }
    } catch (err) {
      console.error('Failed to load event:', err);
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  }

  function updateQuantity(ticketTypeId: string, delta: number) {
    setSelections((prev) =>
      prev.map((s) => {
        if (s.ticketTypeId === ticketTypeId) {
          const newQty = Math.max(0, Math.min(10, s.quantity + delta)); // Max 10 per ticket type
          return { ...s, quantity: newQty };
        }
        return s;
      })
    );
    setAddedToCart(false);
  }

  function getTotalTickets() {
    return selections.reduce((sum, s) => sum + s.quantity, 0);
  }

  function getTotalPrice() {
    return selections.reduce((sum, s) => sum + s.price * s.quantity, 0);
  }

  function handleAddToCart() {
    if (!event) return;

    const selectedTickets = selections.filter((s) => s.quantity > 0);
    if (selectedTickets.length === 0) {
      showToast('error', 'Please select at least one ticket');
      return;
    }

    // Check if enough spots available
    const totalRequested = getTotalTickets();
    if (totalRequested > event.spots_left) {
      showToast('error', `Only ${event.spots_left} spots available`);
      return;
    }

    // Add each selected ticket type to cart
    selectedTickets.forEach((ticket) => {
      addItem({
        productId: `${event.id}-${ticket.ticketTypeId}`,
        name: `${event.name} - ${ticket.ticketTypeName}`,
        price: ticket.price,
        quantity: ticket.quantity,
        type: 'ticket',
        imageUrl: event.image_id || DEFAULT_IMAGES[event.category] || DEFAULT_IMAGES.Other,
        eventId: event.id,
        eventName: event.name,
        eventDate: event.event_date,
        ticketType: ticket.ticketTypeName,
      });
    });

    setAddedToCart(true);
    showToast('success', `Added ${totalRequested} ticket${totalRequested > 1 ? 's' : ''} to cart`);
  }

  function formatEventDate(event: Event) {
    const startDate = new Date(event.event_date);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    if (event.end_date && event.end_date !== event.event_date) {
      const endDate = new Date(event.end_date);
      return `${startDate.toLocaleDateString('en-AU', options)} - ${endDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })}`;
    }
    return startDate.toLocaleDateString('en-AU', options);
  }

  function getAvailabilityInfo(event: Event) {
    const percentLeft = (event.spots_left / event.capacity) * 100;
    if (event.status === 'sold-out' || event.spots_left === 0) {
      return { text: 'Sold Out', color: 'bg-red-100 text-red-700', icon: AlertCircle };
    }
    if (percentLeft <= 10) {
      return { text: 'Almost Sold Out!', color: 'bg-orange-100 text-orange-700', icon: AlertCircle };
    }
    if (percentLeft <= 25) {
      return { text: 'Selling Fast', color: 'bg-yellow-100 text-yellow-700', icon: Info };
    }
    return { text: `${event.spots_left.toLocaleString()} of ${event.capacity.toLocaleString()} spots available`, color: 'bg-green-100 text-green-700', icon: CheckCircle };
  }

  async function handleShare() {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.name,
          text: event?.description || `Check out ${event?.name} at DriveIT NQ!`,
          url,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      showToast('success', 'Link copied to clipboard');
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-red-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The event you are looking for does not exist.'}</p>
          <Link to="/tickets" className="btn btn-primary">
            <ArrowLeft size={18} className="mr-2" />
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const availability = getAvailabilityInfo(event);
  const AvailabilityIcon = availability.icon;
  const isSoldOut = event.spots_left === 0 || event.status === 'sold-out';

  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Back Link */}
        <Link
          to="/tickets"
          className="inline-flex items-center text-gray-600 hover:text-red-600 mb-6 transition-colors"
        >
          <ArrowLeft size={18} className="mr-1" />
          Back to Events
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <div className="relative rounded-2xl overflow-hidden aspect-video bg-gray-100">
              <img
                src={event.image_id || DEFAULT_IMAGES[event.category] || DEFAULT_IMAGES.Other}
                alt={event.name}
                className="w-full h-full object-cover"
              />
              {event.featured === 1 && (
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-full flex items-center gap-1">
                    <Star size={14} className="fill-white" />
                    Featured Event
                  </span>
                </div>
              )}
              <button
                onClick={handleShare}
                className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              >
                <Share2 size={20} className="text-gray-700" />
              </button>
            </div>

            {/* Event Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                  {event.category}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full flex items-center gap-1 ${availability.color}`}>
                  <AvailabilityIcon size={14} />
                  {availability.text}
                </span>
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.name}</h1>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center text-gray-700">
                  <Calendar size={20} className="mr-3 text-red-600" />
                  <div>
                    <p className="font-medium">{formatEventDate(event)}</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-700">
                  <Clock size={20} className="mr-3 text-red-600" />
                  <div>
                    <p className="font-medium">
                      {event.start_time || '09:00'}
                      {event.end_time && ` - ${event.end_time}`}
                    </p>
                    <p className="text-sm text-gray-500">Gates open 1 hour before</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-700">
                  <MapPin size={20} className="mr-3 text-red-600" />
                  <div>
                    <p className="font-medium">{event.location}</p>
                    <p className="text-sm text-gray-500">DriveIT NQ</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-700">
                  <Users size={20} className="mr-3 text-red-600" />
                  <div>
                    <p className="font-medium">{event.capacity.toLocaleString()} capacity</p>
                    <p className="text-sm text-gray-500">{event.spots_left.toLocaleString()} remaining</p>
                  </div>
                </div>
              </div>

              {event.description && (
                <div className="prose prose-gray max-w-none">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">About This Event</h2>
                  <p className="text-gray-600 whitespace-pre-line">{event.description}</p>
                </div>
              )}
            </div>

            {/* What's Included */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-green-600" />
                  Event admission
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-green-600" />
                  Paddock access
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-green-600" />
                  Free parking
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-green-600" />
                  Event program
                </li>
              </ul>
            </div>
          </div>

          {/* Ticket Selection Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Ticket size={20} className="text-red-600" />
                Select Tickets
              </h2>

              {isSoldOut ? (
                <div className="text-center py-8">
                  <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sold Out</h3>
                  <p className="text-gray-600 text-sm">
                    This event has sold out. Check back for future events!
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {event.ticketTypes.map((ticketType) => {
                      const selection = selections.find((s) => s.ticketTypeId === ticketType.id);
                      const quantity = selection?.quantity || 0;

                      return (
                        <div
                          key={ticketType.id}
                          className={`p-4 rounded-xl border-2 transition-colors ${
                            quantity > 0 ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{ticketType.name}</h3>
                              {ticketType.description && (
                                <p className="text-sm text-gray-500">{ticketType.description}</p>
                              )}
                            </div>
                            <span className="font-bold text-lg text-gray-900">
                              {formatPrice(ticketType.price)}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <span className="text-sm text-gray-500">Quantity</span>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuantity(ticketType.id, -1)}
                                disabled={quantity === 0}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  quantity === 0
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                }`}
                              >
                                <Minus size={16} />
                              </button>
                              <span className="w-8 text-center font-semibold text-gray-900">
                                {quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(ticketType.id, 1)}
                                disabled={quantity >= 10}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  quantity >= 10
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary */}
                  {getTotalTickets() > 0 && (
                    <div className="border-t border-gray-200 pt-4 mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>{getTotalTickets()} ticket{getTotalTickets() > 1 ? 's' : ''}</span>
                        <span>Subtotal</span>
                      </div>
                      <div className="flex justify-between text-xl font-bold text-gray-900">
                        <span>Total</span>
                        <span>{formatPrice(getTotalPrice())}</span>
                      </div>
                    </div>
                  )}

                  {/* Add to Cart Button */}
                  {addedToCart ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-2 p-3 bg-green-50 text-green-700 rounded-xl">
                        <CheckCircle size={20} />
                        <span className="font-medium">Added to Cart!</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setSelections(selections.map((s) => ({ ...s, quantity: 0 })));
                            setAddedToCart(false);
                          }}
                          className="btn btn-secondary"
                        >
                          Add More
                        </button>
                        <button
                          onClick={() => navigate('/cart')}
                          className="btn btn-primary"
                        >
                          <ShoppingCart size={18} className="mr-2" />
                          View Cart
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleAddToCart}
                      disabled={getTotalTickets() === 0}
                      className={`btn w-full justify-center ${
                        getTotalTickets() === 0
                          ? 'btn-secondary opacity-50 cursor-not-allowed'
                          : 'btn-primary'
                      }`}
                    >
                      <ShoppingCart size={18} className="mr-2" />
                      {getTotalTickets() === 0 ? 'Select Tickets' : 'Add to Cart'}
                    </button>
                  )}

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Tickets are non-refundable but may be transferred.
                    Children under 12 are free with a paying adult.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
